import { isSameDay, isSameYear, isValid, startOfDay, differenceInDays } from 'date-fns';

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewEncapsulation,
  ViewChild,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewInit,
  HostListener,
  ViewChildren,
  QueryList,
  Optional,
  SkipSelf,
  SimpleChanges,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectionPositionPair } from '@angular/cdk/overlay';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subscription } from 'rxjs';

import { PhxDatePickerMenuComponent } from './menu/menu.component';
import { PhxFocusableItemDirective } from './phx-focusable-item.directive';

import { PhxDatePickerService } from './date-picker.service';
import {
  ISelectedRangeLabel,
  ISelectedRange,
  IMenuItem,
  IHeaderConfig,
  PHX_DATE_FORMAT,
  SelectorSize,
} from './utils/types';
import { DATE_PICKER_MODE, HIDE_YEAR_FORMAT_MAPPER, MAX_MENU_ITEMS_COUNT, SELECTOR_PLACEHOLDERS } from './utils/constants';
import { dateFormatter, isNil } from '../../utils';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PHOENIX_CONTEXTS, PhxContextService } from '../../services/phx-context-service';
import { ArrowLeft, Calendar, ChevronDown } from './../icon/icon-map';
import { PhxDatePickerBase } from './phx-date-picker-base';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { PhxStatsigService } from '../../services/phx-statsig.service';
import { PHX_STATSIG_KEYS } from '../../constants';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider, PhxDateFormatStyle } from '../locale/phx-locale.token';

export const DEFAULT_SIZE: SelectorSize = 'medium';
export type PhxDatePickerDropdownOffset = { x: number; y: number };

@Component({
  selector: 'phx-date-picker',
  templateUrl: './phx-date-picker.component.html',
  styleUrls: ['./phx-date-picker.component.scss'],
  providers: [
    PhxDatePickerService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxDatePickerComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['phxDisabled'],
  host: {
    class: 'phx-date-picker',
    '[class.phx-date-picker-range]': `phxIsRangeEnabled`,
    '[class.phx-date-picker-custom-trigger]': 'phxTriggerTemplate',
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
    '[attr.phx-non-default-selected]': 'isValueSelected && !isDefaultSelection',
    '[class.phx-date-picker-error]': 'shouldShowError',
  },
})
export class PhxDatePickerComponent extends PhxDatePickerBase implements OnInit, OnDestroy, AfterViewInit, OnChanges, ControlValueAccessor {
  @ViewChild('menu') menu: PhxDatePickerMenuComponent;
  @ViewChild('phxElement') phxElement: ElementRef<HTMLElement>;
  @ViewChildren('phxHostElement', { read: PhxFocusableItemDirective })
  private focusableItems: QueryList<PhxFocusableItemDirective>;
  private keyManager: FocusKeyManager<PhxFocusableItemDirective>;

  public prefixCls: string = 'phx-picker';
  public currentVisibleDate: Date = new Date();
  public selectedRange: ISelectedRange;
  public selectedRangeFooterLabels: ISelectedRangeLabel;
  public selectedRangeHeaderLabels: ISelectedRangeLabel;
  public headerConfig: IHeaderConfig;
  public filteredMenuItems: Array<IMenuItem>;

  public overlayHasBackdrop: boolean = false;
  public DATE_PICKER_MODE = DATE_PICKER_MODE;
  public mode: DATE_PICKER_MODE = DATE_PICKER_MODE.DATE;
  public isTemplate: boolean;
  public supportedPositions: ConnectionPositionPair[] = [];
  public overlay: CdkConnectedOverlay;
  public origin: CdkOverlayOrigin;
  public classMap: Map<string, any> = new Map();
  public overlayStyle: Record<string, any> = {};
  public phxStyleType: PHOENIX_CONTEXTS;
  public ChevronDownIcon: string = ChevronDown;
  public CalendarIcon: string = Calendar;
  public ArrowLeftIcon: string = ArrowLeft;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  public isMobileView: boolean = false;
  public phxDatePickerToggleEnabled: boolean = false;

  private onChangeCallback: (value: any) => void = () => {};
  private onTouchedCallback: () => void = () => {};
  private _visible = false;
  private calendarArea: number;
  private _breakpointObserverSubscription: Subscription;
  private _statsigServiceSubscription: Subscription;

  @Input() public phxMenuItems: Array<IMenuItem>;
  @Input() phxIsFullWeekSelectionEnabled: boolean = false;
  // if fixed range enabled then range should be enabled as well
  @Input() phxIsRangeEnabled: boolean = false;
  @Input() phxSelectorSize: SelectorSize = DEFAULT_SIZE;
  @Input() phxInline: boolean = false;
  @Input() phxTriggerTemplate: TemplateRef<any>;
  /** @deprecated No longer has any effect. Will be removed in a future release. */
  @Input() phxFriendlyFormatSelector: boolean = false;
  @Input() phxHideClearButton: boolean = false;
  @Input() phxHideYearInSelector: boolean = false;
  @Input() phxSelectableRangeLimit: number;
  @Input() public phxDropdownOffset: PhxDatePickerDropdownOffset = { x: 0, y: 0 };
  @Input() public phxOpen = false;
  @Input() public phxMultiple: boolean = false;

  @Input() public phxDefaultSelection: ISelectedRange;
  /**
   * Date display format for the selector label.
   * - `'default'` — locale-driven format (e.g. "Jan 15, 2026" for US, "15 Jan 2026" for UK).
   * - `'numeric'` — numeric format (e.g. "03/31/2023"). Not locale-specific.
   * - Legacy date-fns pattern values (e.g. `'MM-dd-yyyy'`) are deprecated and will be
   *   treated as `'default'` when `PHX_LOCALE_PROVIDER` is available.
   */
  @Input() public phxDateFormat: PHX_DATE_FORMAT;

  @Output() public readonly phxOpenChange = new EventEmitter<boolean>();
  @Output() public readonly phxClear = new EventEmitter<void>();
  @Output() public readonly phxValueChange = new EventEmitter<Date | [Date, Date]>();
  @Output() public readonly phxCalendarViewChange = new EventEmitter<Date>();

  public get defautlPlaceholder(): SELECTOR_PLACEHOLDERS {
    return this.phxIsRangeEnabled ? SELECTOR_PLACEHOLDERS.RANGE : SELECTOR_PLACEHOLDERS.DATE;
  }

  public get selectorClasses(): string {
    return `${this.phxIsRangeEnabled ? 'phx-date-picker-range' : ''}
      phx-date-picker-${this.phxSelectorSize}
      ${this.phxDisabled ? 'phx-date-picker-disabled' : ''}
      ${this.isValueSelected && !this.isDefaultSelection ? 'phx-date-picker-selected' : ''}
    `;
  }

  public set visible(value: boolean) {
    const visible = coerceBooleanProperty(value);
    if (this._visible !== visible) {
      this._visible = visible;
    }
  }

  public get visible(): boolean {
    return this._visible;
  }

  public get showResetButton(): boolean {
    return !!this.phxDefaultSelection?.start;
  }

  private set value(selection: Date | ISelectedRange) {
    if (!this.selectedRange || !this.isSameDate(selection)) {
      if (!this.phxIsRangeEnabled) {
        this.updateRange(selection instanceof Date ? selection : selection?.start, null);
      } else {
        if (!selection) this.updateRange();
        else {
          const { start, end }: ISelectedRange = selection as ISelectedRange;
          this.updateRange(start, end);
        }
      }
      if (this.phxIsRangeEnabled && !(selection instanceof Date) && !selection?.end) return;
      this.onChangeCallback(selection);
    }
  }

  private get value() {
    return this.selectedRange;
  }

  constructor(
    public datePickerService: PhxDatePickerService,
    private cdr: ChangeDetectorRef,
    private phxBreakpointObserver: PhxBreakpointObserverService,
    private statsigService: PhxStatsigService,
    @Optional() @SkipSelf() contextService: PhxContextService,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) {
    super();
    this.phxStyleType = contextService?.getType();
  }

  ngAfterViewInit(): void {
    this.keyManager = new FocusKeyManager(this.focusableItems)
      .skipPredicate((item: PhxFocusableItemDirective) => item.skip)
      .withWrap();
    this.calendarArea = this.focusableItems.length - 2; // second last child, dynamic value
  }

  ngOnInit(): void {
    if (!this.value) this.updateRange();
    if (this.showResetButton) this.setDefaultSelection();
    this._statsigServiceSubscription = this.statsigService.getConfigs().subscribe(configs => {
      this.phxDatePickerToggleEnabled = configs[PHX_STATSIG_KEYS.PHX_DATE_PICKER_TOGGLE_ENABLED] ?? false;
      this.cdr.markForCheck();
    });
    this._breakpointObserverSubscription = this.phxBreakpointObserver.smMobile.subscribe(isMobile => {
      this.isMobileView = isMobile;
      this.phxOpen = false;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxMenuItems, phxSelectableRangeLimit } = changes;

    if (phxMenuItems && this.phxMenuItems?.length > MAX_MENU_ITEMS_COUNT) {
      console.warn(`[phxMenuItems] A max of ${MAX_MENU_ITEMS_COUNT} menu items are allowed`);
    }

    if (!isNil(this.phxMenuItems) && (phxSelectableRangeLimit || phxMenuItems)) {
      this.filteredMenuItems = this.filterMenuItems(this.phxMenuItems);
    }
  }

  ngOnDestroy(): void {
    this._breakpointObserverSubscription?.unsubscribe();
    this._statsigServiceSubscription?.unsubscribe();
  }

  public get isValueSelected(): boolean {
    if (!this.value) return false;
    const { start, end } = this.value as ISelectedRange;
    if (this.phxIsRangeEnabled) {
      return !!(start && end);
    } else {
      return !!start;
    }
  }

  public onDropdownToggle(state: boolean): void {
    this.phxOpen = state;
    this.phxOpenChange.emit(state);
    if (!state) {
      this.onTouched();
    }

    if (state) {
      const startDate = this.selectedRange?.start;
      if (startDate && isValid(startDate)) {
        this.updateCalendarView(startDate);
      }
    }
  }

  public onDialogToggle(state: boolean): void {
    if (this.phxDisabled) return;
    this.phxOpen = state;
    this.phxOpenChange.emit(state);
    if (!state) {
      this.onTouched();
    }

    if (state) {
      const startDate = this.selectedRange?.start;
      if (startDate && isValid(startDate)) {
        this.updateCalendarView(startDate);
      }
    }
  }

  public updateCalendarView(value: Date): void {
    this.currentVisibleDate = value;
    this.phxCalendarViewChange.emit(value);
  }

  public onYearSelection(year: Date | null): void {
    this.currentVisibleDate = year;
    this.updateMode(DATE_PICKER_MODE.MONTH);
  }

  public onMonthSelection(month: Date | ISelectedRange | null): void {
    if (month) {
      if (month instanceof Date) {
        this.currentVisibleDate = month;
      } else {
        this.currentVisibleDate = month.start;
      }
      this.updateMode(DATE_PICKER_MODE.DATE);
    }
  }

  public updateMode(mode: DATE_PICKER_MODE): void {
    this.mode = mode;
    this.focusCalendarArea();
  }
  public clear(setValue: boolean = true): void {
    this.closeAndFocus();
    this.reset();
    if (setValue) {
      this.onChangeCallback(null);
      this.phxClear.emit(null);
    }
    this.cdr.markForCheck();
  }

  private reset(): void {
    this.selectedRangeFooterLabels = null;
    this.selectedRangeHeaderLabels = null;
    this.menu?.clearItemSelection();
    this.selectedRange = null;
    this.value = null;
  }

  public selectDateRangeFromMenu(dateRange: ISelectedRange): void {
    this.value = dateRange;
    this.updateRange(this.value.start, this.value.end); // can be deleted if any errors
    this.updateCalendarView(dateRange.start);
    this.closeAndFocus();
  }

  public onDateSelection(selection: ISelectedRange): void {
    const { start, end } = selection;
    let markToCloseOverlay: boolean = false;
    this.menu?.clearItemSelection();
    if (start && !end && !this.phxIsRangeEnabled) {
      this.value = start;
      markToCloseOverlay = true;
    } else if (!start && end) {
      this.value = { start: this.selectedRange.start, end };
    } else {
      this.value = { start, end };
      /**
       * When starting and ending ranges states are concurrent,
       * then selection is considered as completed
       */
      if (start && end) {
        markToCloseOverlay = true;
      }
    }
    if (markToCloseOverlay) {
      this.closeAndFocus();
    }
    this.cdr.markForCheck();
  }

  @HostListener('keydown', ['$event'])
  public onHostKeydown(event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.ARROW_LEFT:
      case KeyboardKeys.ARROW_RIGHT:
      case KeyboardKeys.ARROW_UP:
      case KeyboardKeys.ARROW_DOWN:
      case KeyboardKeys.SPACE:
        event.preventDefault();
        break;
      case KeyboardKeys.ESC:
        event.preventDefault();
        this.onDropdownToggle(false);
    }
  }

  public onKeydown(event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.ARROW_LEFT:
      case KeyboardKeys.ARROW_RIGHT:
      case KeyboardKeys.ARROW_UP:
      case KeyboardKeys.ARROW_DOWN:
      case KeyboardKeys.SPACE:
        event.preventDefault();
        break;
      case KeyboardKeys.TAB:
        event.preventDefault();

        if (this.phxOpen) {
          event.shiftKey ? this.keyManager.setPreviousItemActive() : this.keyManager.setNextItemActive();
        } else {
          this.phxElement?.nativeElement.focus();
        }
        break;
      case KeyboardKeys.ESC:
        event.preventDefault();
        this.closeAndFocus();
        break;
    }
  }

  public onTriggerKeydown(event: KeyboardEvent): void {
    if (event.code === KeyboardKeys.ENTER || event.code === KeyboardKeys.SPACE) {
      event.preventDefault();
      setTimeout(() => {
        this.keyManager.setActiveItem(this.calendarArea);
      }, 100);
      this.onDropdownToggle(true);
    } else if (event.code === KeyboardKeys.ESC) {
      event.preventDefault();
      this.closeAndFocus();
    } else if (event.code === KeyboardKeys.ARROW_LEFT || event.code === KeyboardKeys.ARROW_RIGHT) {
      event.preventDefault();
    }
  }

  public onClickSelector(event: MouseEvent): void {}

  public registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  public onTouched() {
    this.onTouchedCallback();
    this.cdr.markForCheck();
  }

  public writeValue(value: Date | ISelectedRange): void {
    if (value || (value as ISelectedRange)?.start) {
      if (!this.isValidInput(value)) {
        console.warn(`${this.phxIsRangeEnabled ? 'Phx-Date-Range-Picker' : 'Phx-Date-Picker'}: Invalid Input Value`);
        return;
      }

      if (this.isSameDate(value)) return;

      if (!this.phxIsRangeEnabled) {
        this.updateRange(new Date(value as Date));
      } else {
        const { start, end }: { start?: Date; end?: Date } = value as ISelectedRange;
        this.updateRange(new Date(start), new Date(end));
      }
    }
    if (value === null) {
      this.clear(false);
    }
    this.cdr.markForCheck();
  }

  public isValidInput(value: Date | ISelectedRange | any): boolean {
    if (!this.phxIsRangeEnabled) {
      if (value instanceof Date) return isValid(value);
    } else {
      const { start = null, end = null }: ISelectedRange = value as ISelectedRange;
      if (!start && !end) return true;
      if (start instanceof Date && end instanceof Date) {
        return isValid(start) && isValid(end);
      }
    }
    return false;
  }

  public setDefaultSelection(): void {
    if (this.phxDefaultSelection) {
      this.onDateSelection(this.phxDefaultSelection);
      this.updateRange(this.phxDefaultSelection?.start, this.phxDefaultSelection?.end);
    } else {
      this.updateRange();
      this.value = null;
    }
    this.menu?.clearItemSelection();
  }

  public resetToDefaultSelection(): void {
    this.setDefaultSelection();
    this.phxElement?.nativeElement.focus();
  }

  public onSelectorBlur(): void {
    if (!this.phxOpen) {
      this.onTouched();
    }
  }

  public get isDefaultSelection(): boolean {
    return this.phxIsRangeEnabled ? this.isDefaultRangeSelected : this.isDefaultDateSelected;
  }

  public get isNonDefaultRangeSelected(): boolean {
    if (this.showResetButton) {
      return !this.isDefaultSelection;
    }
    return false;
  }

  public get shouldShowError(): boolean {
    return this.phxHasError;
  }

  private get isDefaultRangeSelected(): boolean {
    return (
      this.datePickerService.isSameDate(this.phxDefaultSelection?.start, this.selectedRange?.start) &&
      this.datePickerService.isSameDate(this.phxDefaultSelection?.end, this.selectedRange?.end)
    );
  }

  private get isDefaultDateSelected(): boolean {
    return this.datePickerService.isSameDate(this.phxDefaultSelection?.start, this.selectedRange?.start);
  }

  private isSameDate(value: Date | ISelectedRange): boolean {
    if (value instanceof Date) {
      return isSameDay(value, this.selectedRange?.start);
    } else {
      return (
        isSameDay(value?.start, (this.value as ISelectedRange)?.start) &&
        isSameDay(value?.end, (this.value as ISelectedRange)?.end)
      );
    }
  }

  private getDateFnsLocale() {
    return this.datePickerService.getDateFnsLocale();
  }

  private isCurrentYearRange(): boolean {
    const { start, end } = this.selectedRange;
    const isSameYearRange = isSameYear(start || new Date(), end || new Date());
    return !this.phxIsRangeEnabled || (isSameYearRange && isSameYear(start || new Date(), new Date()));
  }

  private isLegacyFormat(): boolean {
    return !!this.phxDateFormat && this.phxDateFormat in HIDE_YEAR_FORMAT_MAPPER;
  }

  private formatDateLabel(date: Date): string {
    if (!(date instanceof Date) || !isValid(date)) {
      return '';
    }

    const shouldHideYear = this.phxHideYearInSelector && this.isCurrentYearRange();

    if (this.phxLocale) {
      return this.phxLocale.formatDate(date, shouldHideYear ? PhxDateFormatStyle.WITHOUT_YEAR : PhxDateFormatStyle.DEFAULT, { skipTimezone: true });
    }

    return dateFormatter(date, this.getFormatForSelectedRangeLabels(), this.getDateFnsLocale());
  }

  private updateInitialRange(): void {
    this.selectedRangeFooterLabels = {
      ...this.selectedRangeFooterLabels,
      from: this.formatDateLabel(this.selectedRange.start),
    };
    this.selectedRangeHeaderLabels = {
      ...this.selectedRangeHeaderLabels,
      from: this.formatDateLabel(this.selectedRange.start),
    };
  }

  private updateEndingRange(): void {
    const areSameRangeDate = isSameDay((this.value as ISelectedRange).start, (this.value as ISelectedRange).end);

    this.selectedRangeFooterLabels = {
      ...this.selectedRangeFooterLabels,
      to: areSameRangeDate
        ? null
        : this.formatDateLabel((this.value as ISelectedRange).end),
    };

    this.selectedRangeHeaderLabels = {
      ...this.selectedRangeHeaderLabels,
      to: areSameRangeDate
        ? null
        : this.formatDateLabel((this.value as ISelectedRange).end),
    };
  }

  private updateRange(start: Date = null, end: Date = null): void {
    this.selectedRange = {
      ...(end && {
        end: startOfDay(end),
      }),
      ...(start && {
        start: startOfDay(start),
      }),
    };
    this.updateInitialRange();
    if (this.phxIsRangeEnabled) this.updateEndingRange();
    if (this.phxIsRangeEnabled && !this.selectedRange?.end) return;
  }

  /**
   * Fallback format resolver used only when PHX_LOCALE_PROVIDER is not available.
   * When the locale provider is present, formatDateLabel() handles all formatting.
   */
  private getFormatForSelectedRangeLabels(): string {
    const shouldHideYear = this.phxHideYearInSelector && this.isCurrentYearRange();

    if (this.isLegacyFormat()) {
      return shouldHideYear
        ? HIDE_YEAR_FORMAT_MAPPER[this.phxDateFormat]
        : this.phxDateFormat;
    }

    // Default fallback (no locale provider, no specific format)
    const isSameYearRange = isSameYear(
      this.selectedRange.start || new Date(),
      this.selectedRange.end || new Date(),
    );
    return `MMM d${isSameYearRange && shouldHideYear ? '' : ', yyyy'}`;
  }

  private closeAndFocus(): void {
    if (this.phxOpen) {
      if (this.isMobileView) {
        this.onDialogToggle(false);
      } else {
        this.onDropdownToggle(false);
        this.phxElement?.nativeElement.focus();
      }
    }
  }

  private filterMenuItems(items: IMenuItem[]): IMenuItem[] {
    if (!this.phxSelectableRangeLimit) return items.slice(0, MAX_MENU_ITEMS_COUNT);

    return items.slice(0, MAX_MENU_ITEMS_COUNT).filter(menuItem => {
      if ('startDate' in menuItem && 'endDate' in menuItem)
        return differenceInDays(menuItem.endDate, menuItem.startDate) <= this.phxSelectableRangeLimit;
      if ('valueInDays' in menuItem) return Math.abs(menuItem.valueInDays) <= this.phxSelectableRangeLimit;
      return true;
    });
  }

  private focusCalendarArea() {
    this.cdr.detectChanges();
    setTimeout(() => {
      this.keyManager.setActiveItem(this.calendarArea);
    }, 0);
  }
}
